import React, { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Drawer, Segmented, Timeline, Skeleton, Empty, Tooltip } from "antd";
import { DeploymentUnitOutlined } from "@ant-design/icons";
import styled, { keyframes } from "styled-components";

import { useApi, ApiNames } from "../../hooks/api";
import { Routes } from "../../constant/routes";
import { getRunStatusMeta, RUN_STATUS_META, runStatusLabel } from "../../component/Patient/AgenticRuns/runStatuses";
import { GRAY_200, GRAY_500, PRIMARY_600 } from "../../constant/colors";
import { AgenticPanelIcon, AgenticPanelTitle } from "./taskStyles";
import { SectionError } from "./SectionError";
import { patientLabel, formatRelativeTime } from "./taskHelpers";
import { useAutoRefetch } from "./useAutoRefetch";

/**
 * The drawer's two tabs. Mirrors the platform's `RecentRunsScope` — the wire
 * values must stay byte-identical (the endpoint validates them).
 *
 * `null` is the third state the PAGE holds: not open. One piece of state on the
 * parent expresses both "is it open" and "which tab", so an entry point can
 * open straight onto its tab and no open/tab sync effect is needed.
 */
export const ACTIVITY_SCOPE = Object.freeze({
  LIVE: "live",
  RECENT: "recent"
});

/** How many rows the feed asks for. A glanceable feed, not a queue — no pager. */
const FEED_LIMIT = 15;

const DrawerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`;

const FeedScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  /* antd puts the Timeline's last item flush against the container bottom;
     the tail padding keeps the final row clear of the footer rule. */
  padding: 4px 0 8px;
`;

// ─── Row ────────────────────────────────────────────────────────────────
//
// Two lines per event: WHO on top (the patient, the only link), WHAT below
// (the run's status). The relative time sits right-aligned on the top line so
// a scan down the right edge reads as a chronology. No action controls — the
// feed is a read surface; everything actionable lives in the hub's sections.

const RowTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  a {
    color: ${PRIMARY_600};
    font-weight: 600;
    font-size: 13px;
    line-height: 1.3;
  }
`;

const RowWhen = styled.span`
  color: ${GRAY_500};
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const RowStatus = styled.div`
  color: ${GRAY_500};
  font-size: 12px;
  margin-top: 2px;
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.35); opacity: 0.55; }
`;

// The dot carries the status colour from the SAME `RUN_STATUS_META` map the
// patient-page chip uses, so a colour means one thing across the product.
// Motion is reserved for runs actually in flight: animating a terminal row
// would suggest the agent is still working a finished patient.
const StatusDot = styled.span`
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$hex || GRAY_500};
`;

const LiveStatusDot = styled(StatusDot)`
  animation: ${pulse} 2s ease-in-out infinite;
`;

const Footer = styled.div`
  border-top: 1px solid ${GRAY_200};
  padding-top: 12px;
  color: ${GRAY_500};
  font-size: 12px;
`;

// The control and its caption move together, tighter than the drawer's own
// 16px rhythm, so the sentence reads as part of the tabs rather than as the
// feed's first row.
const ScopeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ScopeHint = styled.div`
  color: ${GRAY_500};
  font-size: 12px;
  line-height: 1.35;
`;

/**
 * One event line. The status comes from `getRunStatusMeta` — the patient page's
 * calculator, fed the same input (root + its whole step tree), which is why the
 * feed ships `steps` rather than a server-computed label. The label names who
 * is holding the journey and what they owe; both halves come from there, so
 * this row and the patient page can't drift apart.
 */
const ActivityRow = ({ item, t }) => {
  const meta = getRunStatusMeta(item);
  // Only a run the agent is actually advancing pulses; a parked one is still.
  // Compared by labelKey, not object identity: the waits return a SPREAD copy
  // of their bucket (to carry `detail`) while plain-running returns the bucket
  // itself, so `meta === RUN_STATUS_META.running` held only by accident and a
  // future "spread consistently" edit would have silently killed every pulse.
  const isLive = meta?.labelKey === RUN_STATUS_META.running.labelKey;
  const Dot = isLive ? LiveStatusDot : StatusDot;

  return (
    <Timeline.Item
      dot={<Dot $hex={meta?.hex} data-live={isLive} data-testid={`agentic-activity-dot-${item.agenticRunId}`} />}
      data-testid={`agentic-activity-row-${item.agenticRunId}`}
    >
      <RowTop>
        <Link
          to={Routes.PATIENT_PATH({ patientId: item.patientId })}
          target="_blank"
          rel="noreferrer"
          data-testid={`agentic-activity-patient-${item.agenticRunId}`}
        >
          {patientLabel(item)}
        </Link>
        {/* Last ACTIVITY, matching what the feed orders by. A durable run can
            have started weeks ago, so "started 3 weeks ago" would say nothing
            about what it is doing now. Older payloads without the field fall
            back to the terminal stamp, then the start. */}
        <RowWhen>{formatRelativeTime(item.lastActivityAt || item.completedAt || item.startedAt)}</RowWhen>
      </RowTop>
      <RowStatus>{runStatusLabel(meta, t) || t("agentic_activity.status_unknown")}</RowStatus>
    </Timeline.Item>
  );
};

/**
 * The feed itself. Mounted only while the drawer is open (`destroyOnClose` on
 * the `Drawer`), which is what makes `useAutoRefetch` correct here: it fetches
 * on mount, refreshes each minute while visible, and stops on close. Passing a
 * `paused` flag instead would still fire the first fetch on page load, for a
 * drawer nobody opened.
 */
const AgentActivityFeed = ({ scope, t }) => {
  const [fetchRuns, result, isFetching, fetchError] = useApi(ApiNames.AgenticRecentRuns);

  // `useApi` returns a fresh invoke each render, so the fetch is driven off a
  // ref + a stable callback keyed on `scope` — same pattern as the hub's
  // sections. A tab change gives `refetch` a new identity, which both refires
  // it immediately and re-arms the timer.
  const fetchRunsRef = useRef(fetchRuns);
  fetchRunsRef.current = fetchRuns;
  const refetch = useCallback(() => {
    fetchRunsRef.current({ scope, limit: FEED_LIMIT });
  }, [scope]);
  useAutoRefetch(refetch);

  const items = result?.items ?? [];

  // A failed fetch must not read as a quiet feed — the empty state below would
  // claim the agent did nothing, which is a different (and reassuring) lie.
  if (fetchError && !result) {
    return <SectionError onRetry={refetch} testId="agentic-activity-error" />;
  }
  if (!result && isFetching) {
    // antd's Skeleton drops unknown props, so the test hook lives on a wrapper
    // (same as the stats strip's loading state).
    return (
      <div data-testid="agentic-activity-loading">
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  return (
    <>
      <FeedScroll>
        {items.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              scope === ACTIVITY_SCOPE.LIVE ? t("agentic_activity.empty_live") : t("agentic_activity.empty_recent")
            }
            data-testid="agentic-activity-empty"
          />
        ) : (
          <Timeline data-testid="agentic-activity-timeline">
            {items.map((item) => (
              <ActivityRow key={item.agenticRunId} item={item} t={t} />
            ))}
          </Timeline>
        )}
      </FeedScroll>

      {/* Only when there is something to count — the empty state above already
          says the feed is empty, and "Showing 0 runs" under it says it twice in
          two different tones. */}
      {items.length > 0 && (
        <Footer>
          {/* Truncation is stated, never silent: a capped feed presented as the
            whole story is the same class of error as a stale count.
            Keyed on the page being FULL, not on the response's `total` — that
            field is the size of THIS page (the endpoint returns `rows.length`
            over the same rows it maps to `items`), so `total > items.length`
            was unreachable and the notice never rendered. */}
          <span data-testid="agentic-activity-count">
            {items.length >= FEED_LIMIT
              ? t("agentic_activity.showing_truncated", { shown: items.length })
              : t("agentic_activity.showing", { count: items.length })}
          </span>
        </Footer>
      )}
    </>
  );
};

/**
 * "TM Assistant activity" — the slide-over feed of recent agent runs, opened from the
 * Tasks hub (the "Agent working" stat tile, or the header link).
 *
 * It exists because every patient list in the product is either "needs you"
 * (the hub's four queues) or "broke" (the dashboard's failed-runs panel), so
 * nothing answered "what has the agent been doing?". Deliberately NOT a fifth
 * hub section: those are queues, and a table with a count pill next to them
 * would read as work owed. A drawer costs no page real estate and carries no
 * queue semantics.
 *
 * Controlled by `scope`: a scope value means open on that tab, `null` means
 * closed. The parent owning that single value is what lets each entry point
 * open onto its own tab without a sync effect.
 */
const AgentActivityDrawer = ({ scope, onScopeChange, onClose }) => {
  const { t } = useTranslation();

  // Each tab carries its meaning twice: a tooltip for the tab you are NOT on,
  // and an always-visible caption for the one you are. Hover alone would leave
  // the distinction undiscoverable for anyone who never thinks to hover — and
  // "Live vs Recent" is not self-evident, since a live run is also a recent one.
  const scopeHint = {
    [ACTIVITY_SCOPE.LIVE]: t("agentic_activity.scope_live_hint"),
    [ACTIVITY_SCOPE.RECENT]: t("agentic_activity.scope_recent_hint")
  };
  const scopeOptions = [
    {
      value: ACTIVITY_SCOPE.LIVE,
      label: (
        <Tooltip title={scopeHint[ACTIVITY_SCOPE.LIVE]} placement="bottom">
          <span>{t("agentic_activity.scope_live")}</span>
        </Tooltip>
      )
    },
    {
      value: ACTIVITY_SCOPE.RECENT,
      label: (
        <Tooltip title={scopeHint[ACTIVITY_SCOPE.RECENT]} placement="bottom">
          <span>{t("agentic_activity.scope_recent")}</span>
        </Tooltip>
      )
    }
  ];

  return (
    <Drawer
      open={Boolean(scope)}
      onClose={onClose}
      placement="right"
      width={440}
      destroyOnClose
      title={
        <AgenticPanelTitle>
          <AgenticPanelIcon $size={28}>
            <DeploymentUnitOutlined />
          </AgenticPanelIcon>
          {t("agentic_activity.title")}
        </AgenticPanelTitle>
      }
      bodyStyle={{ display: "flex", flexDirection: "column", padding: "16px 20px" }}
      data-testid="agentic-activity-drawer"
    >
      <DrawerBody>
        <ScopeBlock>
          <Segmented
            block
            options={scopeOptions}
            value={scope}
            onChange={onScopeChange}
            data-testid="agentic-activity-scope"
          />
          <ScopeHint data-testid="agentic-activity-scope-hint">{scopeHint[scope]}</ScopeHint>
        </ScopeBlock>
        <AgentActivityFeed scope={scope} t={t} />
      </DrawerBody>
    </Drawer>
  );
};

export { AgentActivityDrawer };
